import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { CONTENT } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-white/15 py-4 font-mono2 text-sm tracking-wider text-[#f5f3ef] placeholder:text-[#64646e] placeholder:uppercase placeholder:text-[11px] placeholder:tracking-[0.3em] focus:outline-none focus:border-[#38bdf8]/80 transition-colors duration-500";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", inquiry_type: CONTENT.inquiryTypes[0], message: "" });
  const [state, setState] = useState({ status: "idle", error: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending", error: "" });
    try {
      const res = await fetch(`${API}/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = Array.isArray(data.detail) ? data.detail.map((d) => d.msg).join(" ") : data.detail;
        throw new Error(detail || "Senden fehlgeschlagen.");
      }
      setState({ status: "sent", error: "" });
      setForm({ name: "", email: "", inquiry_type: CONTENT.inquiryTypes[0], message: "" });
    } catch (err) {
      setState({ status: "error", error: err.message });
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 md:py-44 px-6 md:px-16 border-t border-white/5 overflow-hidden">
      <div className="absolute -top-40 right-0 w-[600px] h-[600px] bg-[#38bdf8]/6 blur-[140px] rounded-full pointer-events-none" />
      <div className="max-w-[1600px] mx-auto">
        <Reveal>
          <Eyebrow>BOOKING / CONTACT</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-display font-extrabold uppercase tracking-[-0.03em] leading-[0.85] text-[clamp(3.5rem,13vw,11rem)] text-[#f5f3ef] mt-6">
            BOOK <span className="text-[#70c7fa]">TONE</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 text-sm md:text-base text-[#a3a3ac] font-light">
            Bookings, collaborations, press & other inquiries.
          </p>
          <a
            data-testid="booking-email-link"
            href={`mailto:${CONTENT.bookingEmail}`}
            className="mt-4 inline-block font-display font-bold text-xl md:text-3xl text-[#f5f3ef] underline decoration-[#38bdf8]/60 decoration-1 underline-offset-8 hover:text-[#70c7fa] transition-colors duration-300"
          >
            {CONTENT.bookingEmail}
          </a>
        </Reveal>

        <Reveal delay={0.24}>
          <form data-testid="booking-form" onSubmit={submit} className="mt-16 md:mt-24 grid md:grid-cols-2 gap-x-10 gap-y-8 max-w-4xl">
            <input
              data-testid="booking-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="NAME"
              className={inputCls}
            />
            <input
              data-testid="booking-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="E-MAIL"
              className={inputCls}
            />
            <select
              data-testid="booking-type-select"
              value={form.inquiry_type}
              onChange={set("inquiry_type")}
              className={`${inputCls} appearance-none cursor-pointer uppercase text-[11px] tracking-[0.3em] text-[#a3a3ac]`}
            >
              {CONTENT.inquiryTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <div className="hidden md:block" />
            <textarea
              data-testid="booking-message-input"
              required
              rows={5}
              value={form.message}
              onChange={set("message")}
              placeholder="NACHRICHT"
              className={`${inputCls} md:col-span-2 resize-none`}
            />
            <div className="md:col-span-2 flex flex-wrap items-center gap-6 mt-4">
              <button
                data-testid="booking-submit-btn"
                type="submit"
                disabled={state.status === "sending"}
                className="px-12 py-4 bg-[#f5f3ef] text-[#070709] font-display font-bold tracking-wide text-sm hover:bg-[#70c7fa] hover:shadow-[0_0_40px_rgba(56,189,248,0.35)] disabled:opacity-50 transition-all duration-500"
              >
                {state.status === "sending" ? "SENDING…" : "SEND INQUIRY"}
              </button>
              <AnimatePresence mode="wait">
                {state.status === "sent" && (
                  <motion.p
                    key="sent"
                    data-testid="booking-success-msg"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="font-mono2 text-[11px] tracking-[0.25em] text-[#38bdf8] uppercase"
                  >
                    Anfrage gesendet — wir melden uns.
                  </motion.p>
                )}
                {state.status === "error" && (
                  <motion.p
                    key="err"
                    data-testid="booking-error-msg"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="font-mono2 text-[11px] tracking-[0.25em] text-red-400 uppercase"
                  >
                    {state.error}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
